import { NoSsr } from "@mui/base";
import type { PageProps } from "next";
import type { FunctionComponent, ReactNode } from "react";

import { IdleQueue } from "scmp-app/components/common/idle-queue";
import { DynamicGDPRBanner } from "scmp-app/components/gdpr-banner/dynamics";
import { GoogleOneTapLoginContainer } from "scmp-app/components/google-one-tap-login";
import { DynamicPlusArticlePopup } from "scmp-app/components/plus/article-popup/dynamics";
import { DynamicPlusBlurbPopup } from "scmp-app/components/plus/blurb-popup/dynamics";
import { DynamicPlusDetailedListingPopup } from "scmp-app/components/plus/detailed-listing-popup/dynamics";
import { PlusMobileScreen } from "scmp-app/components/plus/mobile-screen";
import { PlusLearnMorePlaceholder } from "scmp-app/pages/plus/learnmore/placeholder";
import { PlusLearnMoreSuspense } from "scmp-app/pages/plus/learnmore/suspense";

import {
  DynamicAppBar,
  DynamicHamburgerMenu,
  DynamicLoginDialog,
  DynamicNewsTicker,
  DynamicPlusPaywallHookRunner,
  DynamicPopupContainer,
  DynamicSimpleHooksRunner,
} from "./dynamics";

export type Props = {
  children: ReactNode;
  pageProps?: PageProps;
};

/**
 * This component is for placing application specific features and also the application header bar
 */
export const AppInitializer: FunctionComponent<Props> = ({ children, pageProps }) => {
  const application = pageProps?.appInitializerConfiguration?.application ?? "scmp";
  const theme = pageProps?.appInitializerConfiguration?.theme ?? "default";

  switch (application) {
    case "plus":
      return (
        <>
          <NoSsr>
            <DynamicSimpleHooksRunner />
            <DynamicLoginDialog />
            <DynamicGDPRBanner />
            <DynamicPlusBlurbPopup />
            <DynamicPlusPaywallHookRunner>
              <PlusMobileScreen />
              {/* Placed for Plus Inline Paywall. Showing inline paywall in desktop; showing PlusMobileScreen in mobile */}
              <DynamicPlusArticlePopup />
              <DynamicPlusDetailedListingPopup />
            </DynamicPlusPaywallHookRunner>
            <IdleQueue>
              <DynamicPopupContainer variant="plus" />
            </IdleQueue>
          </NoSsr>
          <DynamicAppBar pageProps={pageProps}>
            <NoSsr>
              <IdleQueue>
                <DynamicNewsTicker appBarVariant={pageProps?.appBarConfiguration?.variant} />
              </IdleQueue>
            </NoSsr>
          </DynamicAppBar>
          <PlusLearnMoreSuspense fallback={<PlusLearnMorePlaceholder />}>
            {children}
          </PlusLearnMoreSuspense>
        </>
      );
    default:
      switch (theme) {
        case "embed":
        case "print-article":
          return (
            <>
              {children}
              <NoSsr>
                <DynamicSimpleHooksRunner />
                <DynamicGDPRBanner />
              </NoSsr>
            </>
          );
        default:
          return (
            <>
              <DynamicAppBar pageProps={pageProps}>
                <NoSsr>
                  <IdleQueue>
                    <GoogleOneTapLoginContainer />
                    <DynamicNewsTicker appBarVariant={pageProps?.appBarConfiguration?.variant} />
                  </IdleQueue>
                </NoSsr>
              </DynamicAppBar>
              {children}
              <IdleQueue>
                <DynamicPopupContainer />
              </IdleQueue>
              <DynamicHamburgerMenu variant={pageProps?.appBarConfiguration?.variant} />
              <NoSsr>
                <DynamicSimpleHooksRunner />
                <DynamicGDPRBanner />
                <DynamicLoginDialog />
              </NoSsr>
            </>
          );
      }
  }
};

AppInitializer.displayName = "AppInitializer";
